import gsap from "gsap";

// Pixelated page behind modals (auth, download, newsletter, contact, side cart,
// cookie panel).
//
// There's no CSS pixelate filter, and DOM-to-image libraries are too slow here
// (they clone and inline the whole page), so the page is re-painted by hand
// into a small canvas: the elements visible in the viewport are walked in DOM
// order and drawn as background color rects, images, video frames, canvases,
// inline SVGs and text blocks. It's an approximation (no z-index, gradients,
// pseudo-elements, radii), invisible once pixelated.
//
// One full-screen layer sits right under the modals (z-index 999, modals are
// 1000), so each modal's own tinted backdrop still composites on top of it.
// The page is captured once at high res on open; the animation then only
// downsamples that capture, stepping through STEPS pixel sizes, and plays back
// on close.
//
// Usage: open(key, { keep }) / close(key). The key lets several overlays share
// the layer (it stays up until the last one closes). `keep` is a selector for
// an element left out of the capture and kept live above the layer, which then
// sits right under it (e.g. the nav for the mobile menu). window gets "modalbackdrop:open"
// when the first overlay opens and "modalbackdrop:close" when the last one
// closes, so heavy page effects can pause while they're hidden.
// "modalbackdrop:overlay-open" (detail.key) fires on every overlay open.

// Pixel sizes the animation steps through (px), last one is the resting state.
// The first step is shown half transparent: small cells would expose the
// approximated render.
const STEPS = [8, 16, 24, 36, 50];
const FIRST_STEP_OPACITY = 0.5;
const HI_CELL = 2; // px, capture resolution (1 canvas px per HI_CELL CSS px)
const OPEN_DURATION = 0.2;
const CLOSE_DURATION = 0.2;
const TEXT_ALPHA = 0.55;
const OVERSCAN = 0.5; // share of the viewport height walked past each edge

// Overlays themselves (what sits on top of the pixelated page) and full-screen
// effect canvases are never painted.
const SKIP = [
  ".c-auth-modal",
  ".c-download-modal",
  ".c-newsletter-modal",
  "#c-side-cart",
  ".c-contact_window",
  '[id^="tarteaucitron"]',
  ".c-modal-backdrop",
  ".pixel-overlay",
  ".cursor-trail",
  "script",
  "style",
  "noscript",
  "template",
].join(",");

const SVG_PROPS = ["fill", "fill-opacity", "stroke", "stroke-width", "stroke-opacity", "opacity", "visibility", "transform", "transform-origin", "transform-box"];

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

const isTransparent = (c) => !c || c === "transparent" || /rgba\(.*,\s*0\)$/.test(c);

// object-fit: cover source crop
const coverCrop = (sw, sh, dw, dh) => {
  const sr = sw / sh;
  const dr = dw / dh;
  if (sr > dr) {
    const w = sh * dr;
    return [(sw - w) / 2, 0, w, sh];
  }
  const h = sw / dr;
  return [0, (sh - h) / 2, sw, h];
};

// WebGL canvases read back empty outside the frame that rendered them (unless
// preserveDrawingBuffer, which costs a buffer copy every frame). Registered
// canvases are re-rendered and copied synchronously during the capture instead.
const canvasRenderers = new WeakMap();

const snapshotCanvas = (canvas) => {
  canvasRenderers.get(canvas)();
  const copy = document.createElement("canvas");
  copy.width = canvas.width;
  copy.height = canvas.height;
  copy.getContext("2d").drawImage(canvas, 0, 0);
  return copy;
};

const decode = (markup) => {
  const img = new Image();
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(markup);
  return img.decode().then(() => img);
};

class ModalBackdrop {
  constructor() {
    this.owners = new Set();
    this.levels = []; // mip chain of the current capture, full res first
    this.progress = { value: 0 };
    this.tween = null;
    this.token = 0;
    this.lastStep = null;
    this.lastSize = null;
    this.isOpen = false;

    this.bgImages = new Map();
    this.svgCache = new Map();

    this.el = null;
    this.canvas = null;
    this.ctx = null;

    this.onResize = this.onResize.bind(this);
  }

  mount() {
    if (this.el) return;
    this.el = document.createElement("div");
    this.el.className = "c-modal-backdrop";
    this.el.setAttribute("aria-hidden", "true");
    this.canvas = document.createElement("canvas");
    this.el.appendChild(this.canvas);
    document.body.appendChild(this.el);
    this.ctx = this.canvas.getContext("2d");
  }

  open(key = "default", { keep = null } = {}) {
    // Fired on every overlay open (not just the first), before it registers,
    // so other overlays (e.g. the mobile menu) can close themselves first.
    if (!this.owners.has(key)) announceOverlay(key);

    const wasEmpty = this.owners.size === 0;
    this.owners.add(key);
    if (!wasEmpty) return;
    this.isOpen = true;

    this.mount();
    this.el.classList.add("is-visible");
    window.addEventListener("resize", this.onResize);

    // Kept element: layer right under its z-index instead of under the modals
    const kept = keep && document.querySelector(keep);
    const keptZ = kept && parseInt(getComputedStyle(kept).zIndex, 10);
    this.el.style.zIndex = Number.isFinite(keptZ) ? keptZ - 1 : "";

    // Reopened during the close animation: the capture is still valid (if it
    // left out the same element)
    const sameKeep = keep === this.keep;
    this.keep = keep;
    if (sameKeep && this.levels.length && this.progress.value > 0) {
      window.dispatchEvent(new CustomEvent("modalbackdrop:open"));
      this.animateTo(1, OPEN_DURATION);
      return;
    }

    const token = ++this.token;
    this.progress.value = 0;
    this.draw();
    // Capture first (reads everything synchronously up to the SVG decoding),
    // then let page effects pause: they're drawn from their current frame.
    const job = this.capture(keep ? `${SKIP},${keep}` : SKIP);
    window.dispatchEvent(new CustomEvent("modalbackdrop:open"));
    job.then((levels) => {
      // Closed (or reopened) while SVGs were decoding
      if (token !== this.token || !this.owners.size) return;
      this.levels = levels;
      this.animateTo(1, OPEN_DURATION);
    });
  }

  close(key = "default") {
    if (!this.owners.delete(key) || this.owners.size) return;
    window.removeEventListener("resize", this.onResize);
    this.isOpen = false;
    window.dispatchEvent(new CustomEvent("modalbackdrop:close"));

    if (!this.levels.length) {
      this.token++; // drop the pending capture
      this.hide();
      return;
    }
    this.animateTo(0, CLOSE_DURATION, () => this.hide());
  }

  hide() {
    this.tween?.kill();
    this.progress.value = 0;
    this.levels = [];
    this.lastStep = null;
    this.el?.classList.remove("is-visible");
  }

  // Linear: each step holds the same time
  animateTo(value, duration, onComplete) {
    this.tween?.kill();
    this.tween = gsap.to(this.progress, {
      value,
      duration: reducedMotion.matches ? 0 : duration,
      ease: "none",
      onUpdate: () => this.draw(),
      onComplete,
    });
  }

  onResize() {
    this.draw();
  }

  // Draws the capture at the current progress, quantized to a step of STEPS.
  // The grid rarely fits the viewport exactly, so it's centered and the canvas
  // overhangs by less than a cell (clipped by the layer); the capture is
  // stretched a hair to cover it, which doesn't show once pixelated.
  draw() {
    const p = this.progress.value;
    const step = p <= 0 ? -1 : Math.min(STEPS.length - 1, Math.floor(p * STEPS.length));
    const W = window.innerWidth;
    const H = window.innerHeight;
    const size = `${W}x${H}`;
    if (step === this.lastStep && size === this.lastSize) return;
    this.lastStep = step;
    this.lastSize = size;

    this.el.style.opacity = step < 0 ? 0 : step === 0 ? FIRST_STEP_OPACITY : 1;
    if (step < 0 || !this.levels.length) return;

    const cell = STEPS[step];
    const sw = Math.ceil(W / cell);
    const sh = Math.ceil(H / cell);

    // Smallest mip level still at least as large as the target: averages
    // properly, so the colors don't shimmer while the cells grow.
    let src = this.levels[0];
    for (const level of this.levels) {
      if (level.width < sw || level.height < sh) break;
      src = level;
    }

    if (this.canvas.width !== sw) this.canvas.width = sw;
    if (this.canvas.height !== sh) this.canvas.height = sh;
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.drawImage(src, 0, 0, sw, sh);

    Object.assign(this.canvas.style, {
      left: `${-(sw * cell - W) / 2}px`,
      top: `${-(sh * cell - H) / 2}px`,
      width: `${sw * cell}px`,
      height: `${sh * cell}px`,
    });
  }

  // Paints the viewport into a canvas at 1/HI_CELL scale, then builds its mip
  // chain (successive halvings). Async only because inline SVGs are decoded as
  // images; everything is read synchronously, before the modal changes the page.
  async capture(skip) {
    const W = window.innerWidth;
    const H = window.innerHeight;

    const out = document.createElement("canvas");
    out.width = Math.ceil(W / HI_CELL);
    out.height = Math.ceil(H / HI_CELL);
    const ctx = out.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.scale(1 / HI_CELL, 1 / HI_CELL); // draw in CSS px

    const bodyBg = getComputedStyle(document.body).backgroundColor;
    const rootBg = getComputedStyle(document.documentElement).backgroundColor;
    ctx.fillStyle = !isTransparent(bodyBg) ? bodyBg : !isTransparent(rootBg) ? rootBg : "#fff";
    ctx.fillRect(0, 0, W, H);

    const VIEW = { l: 0, t: 0, r: W, b: H };
    const over = H * OVERSCAN;
    const ZONE = { l: -over, t: -over, r: W + over, b: H + over };

    // Fixed/sticky elements (nav...) sit on top of the flow but often come
    // first in the DOM: set aside and painted last, sorted by z-index.
    const deferred = [];
    // Draws are queued so SVG decoding can finish without breaking paint order
    const ops = [];
    const pending = [];

    // Depth-first, carrying the clip rect of overflow-clipping ancestors
    const walk = (el, clip, isDeferred = false) => {
      if (el.matches(skip)) return;

      // Cheap reject before getComputedStyle (the expensive part): subtrees
      // entirely outside viewport + overscan are skipped. offsetParent is null
      // for position:fixed, whose flow position says nothing: full check.
      const r = el.getBoundingClientRect();
      if (el.offsetParent !== null && (r.bottom < ZONE.t || r.top > ZONE.b || r.right < ZONE.l || r.left > ZONE.r)) return;

      const cs = getComputedStyle(el);
      if (cs.display === "none" || cs.visibility === "hidden" || +cs.opacity === 0) return;

      const fixed = cs.position === "fixed";
      if (!isDeferred && (fixed || cs.position === "sticky")) {
        deferred.push({ el, clip, z: parseInt(cs.zIndex, 10) || 0, order: deferred.length });
        return;
      }
      const c = fixed ? VIEW : clip;
      const visible = r.width > 0 && r.height > 0 && r.right > c.l && r.left < c.r && r.bottom > c.t && r.top < c.b;

      if (visible) {
        const isLogo = el.matches(".c-navigation__logo-video");
        const svg = el instanceof SVGSVGElement || isLogo ? { img: null } : null;
        if (svg) {
          const job = isLogo ? this.rasterLogo(el, r) : this.rasterSvg(el, r);
          pending.push(job.then((img) => (svg.img = img), () => {}));
        }
        // Values read now: cs is live and the modal is about to change the page
        const v = {
          opacity: Math.min(1, +cs.opacity),
          bg: cs.backgroundColor,
          bgImg: cs.backgroundImage,
          fit: cs.objectFit,
          color: cs.color,
          // Now, in the same task as its render: the draw runs after SVG decoding
          canvas: canvasRenderers.has(el) ? snapshotCanvas(el) : el,
        };
        ops.push(() => {
          ctx.save();
          ctx.beginPath();
          ctx.rect(c.l, c.t, c.r - c.l, c.b - c.t);
          ctx.clip();
          ctx.globalAlpha = v.opacity;
          this.paint(ctx, el, r, v, svg, isLogo);
          ctx.restore();
        });
      }

      if (el instanceof SVGSVGElement) return; // drawn as one image

      let next = c;
      if (cs.overflowX !== "visible" || cs.overflowY !== "visible") {
        next = { l: Math.max(c.l, r.left), t: Math.max(c.t, r.top), r: Math.min(c.r, r.right), b: Math.min(c.b, r.bottom) };
        if (next.r <= next.l || next.b <= next.t) return;
      }
      for (const child of el.children) walk(child, next, isDeferred);
      if (el.shadowRoot) for (const child of el.shadowRoot.children) walk(child, next, isDeferred);
    };

    walk(document.body, VIEW);
    deferred.sort((a, b) => a.z - b.z || a.order - b.order).forEach(({ el, clip }) => walk(el, clip, true));

    if (pending.length) await Promise.all(pending);
    ops.forEach((draw) => draw());

    const levels = [out];
    let cur = out;
    while (cur.width > 8 && cur.height > 8) {
      const next = document.createElement("canvas");
      next.width = Math.ceil(cur.width / 2);
      next.height = Math.ceil(cur.height / 2);
      const nctx = next.getContext("2d");
      nctx.imageSmoothingQuality = "high";
      nctx.drawImage(cur, 0, 0, next.width, next.height);
      levels.push(next);
      cur = next;
    }
    return levels;
  }

  paint(ctx, el, r, v, svg, isLogo) {
    if (!isTransparent(v.bg)) {
      ctx.fillStyle = v.bg;
      ctx.fillRect(r.left, r.top, r.width, r.height);
    }

    const url = v.bgImg.match(/url\(["']?(.*?)["']?\)/);
    const bg = url && this.bgImage(url[1]);
    try {
      if (bg) ctx.drawImage(bg, ...coverCrop(bg.naturalWidth, bg.naturalHeight, r.width, r.height), r.left, r.top, r.width, r.height);

      if (svg?.img) {
        ctx.drawImage(svg.img, r.left, r.top, r.width, r.height);
      } else if (el instanceof HTMLImageElement && el.complete && el.naturalWidth) {
        const crop = v.fit === "cover" ? coverCrop(el.naturalWidth, el.naturalHeight, r.width, r.height) : [0, 0, el.naturalWidth, el.naturalHeight];
        ctx.drawImage(el, ...crop, r.left, r.top, r.width, r.height);
      } else if (el instanceof HTMLVideoElement && el.readyState >= 2 && (!isLogo || !svg?.img)) {
        // The logo video only as a fallback, if its SVG stand-in failed
        const crop = v.fit === "cover" ? coverCrop(el.videoWidth, el.videoHeight, r.width, r.height) : [0, 0, el.videoWidth, el.videoHeight];
        ctx.drawImage(el, ...crop, r.left, r.top, r.width, r.height);
      } else if (el instanceof HTMLCanvasElement && el.width) {
        ctx.drawImage(v.canvas, r.left, r.top, r.width, r.height);
      }
    } catch (e) {
      // Broken or not-yet-decodable source: skip it
    }

    // Text: each line becomes a block in the text color
    for (const n of el.childNodes) {
      if (n.nodeType !== Node.TEXT_NODE || !n.textContent.trim()) continue;
      const range = document.createRange();
      range.selectNodeContents(n);
      ctx.fillStyle = v.color;
      ctx.globalAlpha = v.opacity * TEXT_ALPHA;
      for (const tr of range.getClientRects()) ctx.fillRect(tr.left, tr.top + tr.height * 0.15, tr.width, tr.height * 0.7);
    }
  }

  // background-image urls -> Image objects, loaded once. Not ready on the
  // first capture: it shows up on the next ones.
  bgImage(url) {
    let img = this.bgImages.get(url);
    if (!img) {
      img = new Image();
      img.src = url;
      this.bgImages.set(url, img);
    }
    return img.complete && img.naturalWidth ? img : null;
  }

  // Inline <svg> -> Image. Computed styles are copied onto the clone so CSS
  // fills (classes, currentColor) and GSAP transforms survive serialization.
  rasterSvg(svg, r) {
    const clone = svg.cloneNode(true);
    const src = [svg, ...svg.querySelectorAll("*")];
    const dst = [clone, ...clone.querySelectorAll("*")];
    src.forEach((node, i) => {
      const cs = getComputedStyle(node);
      SVG_PROPS.forEach((p) => dst[i].style.setProperty(p, cs.getPropertyValue(p)));
    });
    clone.style.removeProperty("transform"); // the root's own transform is already in its rect
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("width", r.width);
    clone.setAttribute("height", r.height);

    const markup = new XMLSerializer().serializeToString(clone);
    if (!this.svgCache.has(markup)) this.svgCache.set(markup, decode(markup));
    return this.svgCache.get(markup);
  }

  // The header logo is a video (with alpha, HEVC on Safari, unreliable in a
  // canvas), but it's the footer SVG artwork: drawn instead, in the nav's text
  // color, at rest (no reveal offset), cropped to its paths and fitted in the
  // video's box.
  rasterLogo(video, r) {
    const src = document.querySelector(".c-footer__logo--desktop");
    if (!src) return Promise.reject(new Error("footer logo not found"));
    const color = getComputedStyle(video.closest("a") || video).color;
    const key = `logo|${color}|${r.width}x${r.height}`;
    if (this.svgCache.has(key)) return this.svgCache.get(key);

    const box = { x1: Infinity, y1: Infinity, x2: -Infinity, y2: -Infinity };
    src.querySelectorAll("path").forEach((p) => {
      const b = p.getBBox(); // own geometry, ignores the GSAP translate
      box.x1 = Math.min(box.x1, b.x);
      box.y1 = Math.min(box.y1, b.y);
      box.x2 = Math.max(box.x2, b.x + b.width);
      box.y2 = Math.max(box.y2, b.y + b.height);
    });
    // Hidden SVG (mobile): no geometry, fall back to the video frame
    if (!(box.x2 > box.x1)) return Promise.reject(new Error("footer logo has no geometry"));

    const clone = src.cloneNode(true);
    clone.querySelectorAll("path").forEach((p) => {
      p.removeAttribute("style");
      p.removeAttribute("transform");
      p.setAttribute("fill", color);
    });
    clone.removeAttribute("class");
    clone.removeAttribute("style");
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("viewBox", `${box.x1} ${box.y1} ${box.x2 - box.x1} ${box.y2 - box.y1}`);
    clone.setAttribute("preserveAspectRatio", "xMidYMid meet");
    clone.setAttribute("width", r.width);
    clone.setAttribute("height", r.height);

    const job = decode(new XMLSerializer().serializeToString(clone));
    this.svgCache.set(key, job);
    return job;
  }
}

// For overlays that open without the pixelated layer (full-screen ones on
// mobile): still lets the others (e.g. the menu) react to the open.
export function announceOverlay(key) {
  window.dispatchEvent(new CustomEvent("modalbackdrop:overlay-open", { detail: { key } }));
}

const modalBackdrop = new ModalBackdrop();

export default modalBackdrop;
export const openModalBackdrop = (key, options) => modalBackdrop.open(key, options);
export const closeModalBackdrop = (key) => modalBackdrop.close(key);
// render: draws the canvas's current state synchronously (e.g. a WebGL app's render())
export const registerCanvas = (canvas, render) => canvasRenderers.set(canvas, render);
